import dayjs from 'dayjs';
import dynamic from 'next/dynamic';
import Image from 'next/image';
import Link from 'next/link';
import { useRouter } from 'next/router';
import { FC } from 'react';
import useSWR from 'swr';
import { formatPrice } from 'utils/hooks/usePrice';

import { SeatsProps } from '~components/Purchase/Seats/Seats';
import NoSsr from '~components/common/NoSsr';
import Loading from '~components/ui/Loading';
import { getFromAPI } from '~utils/api/common';
import usePurchase from '~utils/hooks/usePurchase';

import { SessionSeatsResponse, ShowWithMovie } from '~types';

import UserLoyalty from '../UserLoyalty';
import s from './Start.module.css';

interface Props {
  show?: ShowWithMovie;
}

const Seats = dynamic<SeatsProps>(() => import('../Seats'), {
  ssr: false,
  loading: () => <span>Cargando asientos...</span>,
}) as typeof import('../Seats').default;

const Tickets = dynamic(() => import('../Tickets/Tickets'), {
  ssr: false,
  loading: () => <span>Cargando boletos...</span>,
}) as typeof import('../Tickets/Tickets').default;

const isOnTime = (time: string, tz: string = 'America/Mazatlan') => {
  return dayjs(time).tz(tz).isAfter(dayjs().tz(tz).subtract(20, 'minutes'));
};

const PurchaseStart: FC<Props> = ({ show }) => {
  const router = useRouter();
  const { total, addSeat, removeSeat } = usePurchase();
  const error = show?.sessionId === undefined;
  const showTickets = show?.ticketPrices || [];
  // Obtiene el layout de asientos
  const {
    data,
    isLoading,
    error: seatsError,
  } = useSWR<SessionSeatsResponse>(
    show?.schedule && isOnTime(show?.schedule, show?.Cinema?.timezone) && show?.Cinema?.onlineSale
      ? `/shows/seats/${show?.sessionId}`
      : null,
    (url: string) => getFromAPI(url, { notifyErrors: false }),
  );

  return (
    <section className="container">
      <div className="my-10">
        <h1 className="h2 sm:h1">Ponte cómodo, elige tus asientos</h1>
        <p>Aparta tu lugar dando click en los asientos disponibles. Máximo 10 boletos.</p>
      </div>
      <Loading
        show={isLoading}
        size="large"
        label="Cargando asientos de la sala."
        className="!bg-white/80 dark:!bg-neutral-900/80"
      >
        <div data-testid={`loading-${isLoading}`} className={s.contentWrapper}>
          <div className={s.seatsWrapper}>
            {error || (seatsError && !data) ? (
              <div className="relative w-full mb-16 md:mb-0">
                <Image
                  src="/img/seats-error.png"
                  className="object-cover"
                  width={774}
                  height={483}
                  alt="Imagen de error en los asientos"
                />
                <div className="text-support absolute inset-0 dark:bg-main/90 callout">
                  <p className="relative top-[60%] max-w-[400px] mx-auto text-center bg-gradient-to-b from-[#FFFFFF] to-[#FFFFFF00] dark:from-neutral-900 dark:to-neutral-900/0 from-30%">
                    <strong>¡Lo sentimos!</strong> No pudimos encontrar los asientos disponibles
                    para esta sala en este momento. Es posible que los boletos se hayan agotado o
                    que haya un problema técnico.
                  </p>
                </div>
              </div>
            ) : (
              <>
                <p className="mb-4">
                  <span className="font-semibold">Complejo:</span> {show?.Cinema?.name} -{' '}
                  <span data-testid="screen-name">{show?.screenName}</span>{' '}
                  {!!show?.roomType?.premiere && (
                    <span className={s.premiereTag}>Sala Premiere</span>
                  )}
                  {!!show?.roomType?.comfort && <span className={s.comfortTag}>Sala confort</span>}
                </p>
                <Seats
                  data={data}
                  roomType={show?.roomType}
                  onAddSeat={(pickedSeat) => {
                    const ticket = showTickets[0];
                    const pickedTicket = { ...ticket, id: pickedSeat.id };
                    addSeat(pickedSeat, pickedTicket);
                  }}
                  onRemoveSeat={(seatId) => {
                    removeSeat(seatId);
                  }}
                />
              </>
            )}
          </div>
          <div className={s.ticketSection}>
            <h2 className="h2">Asientos seleccionados</h2>
            <p>Aquí puedes cambiar el tipo de boleto para cada asiento</p>
            <Tickets ticketsPrices={showTickets} />
          </div>
          <div className="col-span-12">
            <div className={s.paymentSection}>
              <UserLoyalty total={total} />
              <NoSsr>
                <Link
                  href={router.asPath.replace('step=inicio', 'step=pago')}
                  shallow
                  key={total || 'paybutton'}
                  legacyBehavior
                >
                  <button
                    data-testid="pay-button"
                    disabled={total === 0}
                    className={s.paymentButton}
                  >
                    <span>{total === 0 ? 'Pagar' : `Pagar (${formatPrice(total)})`}</span>
                  </button>
                </Link>
              </NoSsr>
            </div>
          </div>
        </div>
      </Loading>
    </section>
  );
};

export default PurchaseStart;
